/**
 * GrowthShortlist — iter101 · Vue Direction (Phase 2)
 *
 * **Brief CEO §20** : la Direction ne lit pas 140 CV. Elle voit uniquement
 * les candidats **préqualifiés** avec :
 *   - Score dossier + Score préqualif + Score avant entretien
 *   - Formation, synthèse, forces démontrées, red flags
 *   - Recommandation IA
 *   - Lien vers la fiche complète (CV + réécoute appel)
 *
 * **Pas de seuil figé** — vue triée par score avant entretien décroissant.
 */
import { AlertCircle, ExternalLink, Loader2, PhoneCall, Sparkles, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";
import { formatDate } from "../lib/format";

const CAT_META = {
    top:         { emoji: "⭐", label: "PRIORITAIRE",  tone: "bg-yellow-50 text-yellow-800 border-yellow-300" },
    recommended: { emoji: "🟢", label: "RECOMMANDÉ",   tone: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    reserve:     { emoji: "🟡", label: "RÉSERVE",      tone: "bg-amber-50 text-amber-800 border-amber-300" },
    non_prio:    { emoji: "🔴", label: "NON PRIO",     tone: "bg-slate-100 text-slate-600 border-slate-300" },
};


export default function GrowthShortlist() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/growth/shortlist?limit=50")
            .then(({ data }) => setList(data || []))
            .catch((e) => toast.error(e.response?.data?.detail || "Erreur de chargement"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="growth-shortlist-page">
            <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 text-purple-700 border border-purple-200 text-[10px] font-black uppercase tracking-widest mb-2">
                    <Sparkles className="h-3 w-3" /> Shortlist Direction · Growth
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-shariti-black tracking-tight">
                    Shortlist Direction
                </h1>
                <p className="text-base font-semibold text-slate-500 mt-1">
                    {list.length} candidat{list.length > 1 ? "s" : ""} préqualifié{list.length > 1 ? "s" : ""} — triés par score avant entretien
                </p>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : list.length === 0 ? (
                <div className="text-center py-16 rounded-2xl bg-slate-50 border border-slate-100">
                    <PhoneCall className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="font-bold text-slate-500">Aucune préqualification effectuée pour le moment</p>
                    <p className="text-xs text-slate-400 mt-1">
                        Ouvre <Link to="/growth" className="text-shariti-orange font-bold">/growth</Link> et lance une préqualification depuis un candidat.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((c, i) => {
                        const meta = CAT_META[c.growth_prequal_category] || CAT_META.non_prio;
                        return (
                            <div
                                key={c.id}
                                data-testid={`growth-shortlist-row-${c.id}`}
                                className="rounded-2xl bg-white border-2 border-slate-100 hover:border-shariti-orange/40 p-5 transition"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="shrink-0 w-12 h-12 rounded-xl bg-shariti-cream flex items-center justify-center">
                                        <span className="text-shariti-orange font-black text-lg">#{i + 1}</span>
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Link to={`/candidats/${c.id}`} className="text-xl font-black text-shariti-black hover:text-shariti-orange">
                                                {c.first_name} {c.last_name}
                                            </Link>
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border-2 ${meta.tone}`}>
                                                {meta.emoji} {meta.label}
                                            </span>
                                        </div>
                                        <div className="text-xs font-semibold text-slate-500 mt-0.5">
                                            {c.city || "—"} · {c.email} · Préqualifié le {formatDate(c.growth_prequal_scored_at)}
                                        </div>

                                        {c.growth_prequal_summary && (
                                            <p className="text-sm text-slate-700 italic mt-2 leading-snug">
                                                {c.growth_prequal_summary}
                                            </p>
                                        )}

                                        {c.growth_prequal_red_flags?.length > 0 && (
                                            <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-800 font-semibold">
                                                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                                <span>{c.growth_prequal_red_flags[0]}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="shrink-0 flex flex-col items-end gap-1 text-right">
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <div className="text-[9px] font-black text-slate-500 uppercase">Dossier</div>
                                                <div className="text-lg font-black text-shariti-orange">{c.growth_score || "—"}</div>
                                            </div>
                                            <div className="border-l pl-2">
                                                <div className="text-[9px] font-black text-slate-500 uppercase">Préqualif</div>
                                                <div className="text-lg font-black text-blue-600">{c.growth_prequal_score || "—"}</div>
                                            </div>
                                            <div className="border-l pl-2">
                                                <div className="text-[9px] font-black text-purple-500 uppercase">Avant entr.</div>
                                                <div className="text-2xl font-black text-purple-700">{c.growth_avant_entretien_score || "—"}</div>
                                            </div>
                                        </div>
                                        <Link
                                            to={`/candidats/${c.id}`}
                                            data-testid={`growth-shortlist-open-${c.id}`}
                                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-black text-shariti-orange hover:underline"
                                        >
                                            Fiche complète · CV · réécoute <ExternalLink className="h-3 w-3" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
